import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PricingPlan } from "@/model/interface/PricingPlan";
import React from "react";
import RateField from "./RateField";

interface PlanDataRatesSectionProps {
  editing: Partial<PricingPlan>;
  onField: (field: string, value: unknown) => void;
}

export default function PlanDataRatesSection({ editing, onField }: PlanDataRatesSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Data and compliance rates</CardTitle>
        <CardDescription>
          Lookups, Do Not Call checks, skip tracing and two-factor texts. Skip trace and Do Not Call rates are per provider
          credit.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <RateField
          label="Line type lookup (per number)"
          baseRate={editing.lineTypeLookupBaseRate ?? 0}
          markupType={editing.lineTypeLookupMarkupType ?? "FLAT"}
          markupAmount={editing.lineTypeLookupMarkupAmount ?? 0}
          onBaseRateChange={(newBaseRate) => onField("lineTypeLookupBaseRate", newBaseRate)}
          onMarkupTypeChange={(newMarkupType) => onField("lineTypeLookupMarkupType", newMarkupType)}
          onMarkupAmountChange={(newMarkupAmount) => onField("lineTypeLookupMarkupAmount", newMarkupAmount)}
        />
        <RateField
          label="Caller ID lookup (per number)"
          baseRate={editing.callerIdLookupBaseRate ?? 0}
          markupType={editing.callerIdLookupMarkupType ?? "FLAT"}
          markupAmount={editing.callerIdLookupMarkupAmount ?? 0}
          onBaseRateChange={(newBaseRate) => onField("callerIdLookupBaseRate", newBaseRate)}
          onMarkupTypeChange={(newMarkupType) => onField("callerIdLookupMarkupType", newMarkupType)}
          onMarkupAmountChange={(newMarkupAmount) => onField("callerIdLookupMarkupAmount", newMarkupAmount)}
        />
        <RateField
          label="Do Not Call check, national, state and litigator lists (per credit)"
          baseRate={editing.tracerfyDncBaseRate ?? 0}
          markupType={editing.tracerfyDncMarkupType ?? "FLAT"}
          markupAmount={editing.tracerfyDncMarkupAmount ?? 0}
          onBaseRateChange={(newBaseRate) => onField("tracerfyDncBaseRate", newBaseRate)}
          onMarkupTypeChange={(newMarkupType) => onField("tracerfyDncMarkupType", newMarkupType)}
          onMarkupAmountChange={(newMarkupAmount) => onField("tracerfyDncMarkupAmount", newMarkupAmount)}
        />
        <RateField
          label="Do Not Call check, national list only (per number)"
          baseRate={editing.datazappDncBaseRate ?? 0}
          markupType={editing.datazappDncMarkupType ?? "FLAT"}
          markupAmount={editing.datazappDncMarkupAmount ?? 0}
          onBaseRateChange={(newBaseRate) => onField("datazappDncBaseRate", newBaseRate)}
          onMarkupTypeChange={(newMarkupType) => onField("datazappDncMarkupType", newMarkupType)}
          onMarkupAmountChange={(newMarkupAmount) => onField("datazappDncMarkupAmount", newMarkupAmount)}
        />
        <RateField
          label="Skip trace (per credit, single traces)"
          baseRate={editing.skipTraceBaseRate ?? 0}
          markupType={editing.skipTraceMarkupType ?? "FLAT"}
          markupAmount={editing.skipTraceMarkupAmount ?? 0}
          onBaseRateChange={(newBaseRate) => onField("skipTraceBaseRate", newBaseRate)}
          onMarkupTypeChange={(newMarkupType) => onField("skipTraceMarkupType", newMarkupType)}
          onMarkupAmountChange={(newMarkupAmount) => onField("skipTraceMarkupAmount", newMarkupAmount)}
        />
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Skip trace bulk markup (same type as above)</Label>
            <Input
              type="number"
              step="0.01"
              value={editing.skipTraceBulkMarkupAmount ?? 0}
              onChange={(changeEvent) => onField("skipTraceBulkMarkupAmount", parseFloat(changeEvent.target.value) || 0)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Bulk from (contacts)</Label>
            <Input
              type="number"
              min={1}
              value={editing.skipTraceBulkMinimum ?? 1}
              onChange={(changeEvent) => onField("skipTraceBulkMinimum", parseInt(changeEvent.target.value) || 1)}
            />
          </div>
        </div>
        <RateField
          label="Two-factor text (per message)"
          baseRate={editing.twoFactorSmsBaseRate ?? 0}
          markupType={editing.twoFactorSmsMarkupType ?? "FLAT"}
          markupAmount={editing.twoFactorSmsMarkupAmount ?? 0}
          onBaseRateChange={(newBaseRate) => onField("twoFactorSmsBaseRate", newBaseRate)}
          onMarkupTypeChange={(newMarkupType) => onField("twoFactorSmsMarkupType", newMarkupType)}
          onMarkupAmountChange={(newMarkupAmount) => onField("twoFactorSmsMarkupAmount", newMarkupAmount)}
        />
        <RateField
          label="Two-factor verification (per code accepted)"
          baseRate={editing.twoFactorVerificationBaseRate ?? 0}
          markupType={editing.twoFactorVerificationMarkupType ?? "FLAT"}
          markupAmount={editing.twoFactorVerificationMarkupAmount ?? 0}
          onBaseRateChange={(newBaseRate) => onField("twoFactorVerificationBaseRate", newBaseRate)}
          onMarkupTypeChange={(newMarkupType) => onField("twoFactorVerificationMarkupType", newMarkupType)}
          onMarkupAmountChange={(newMarkupAmount) => onField("twoFactorVerificationMarkupAmount", newMarkupAmount)}
        />
      </CardContent>
    </Card>
  );
}
